/**
 * ASSIST-01 — one shared answer to "has this user joined the waitlist?".
 *
 * A property page mounts the Assist card twice per tree (the mobile copy after
 * the agent card, the desktop copy in the sticky sidebar), and on web a
 * listing click can mount both trees for a moment. They must agree: one
 * get_my_assist_interest call per user, one register call per tap, and a
 * "You're on the list." in every copy as soon as either copy succeeds.
 *
 * The RPCs are injected, so this module imports nothing and `tsx` tests can
 * drive it with fakes. components/assist/AssistCard.tsx creates the real one.
 */

import type { AssistMineOutcome, AssistRegisterOutcome } from './config';

/**
 * unknown      nothing asked yet
 * loading      get_my_assist_interest in flight
 * none         not joined (or the read failed: registering is idempotent, so
 *              offering the button again is safe)
 * joined       on the list
 * unavailable  the server refused the read (assist_not_live): hide the card
 */
export type AssistInterestStatus = 'unknown' | 'loading' | 'none' | 'joined' | 'unavailable';

export interface AssistInterestApi {
  getMine(): Promise<AssistMineOutcome>;
  register(listingId: string): Promise<AssistRegisterOutcome>;
}

export interface AssistInterestStore {
  status(userId: string): AssistInterestStatus;
  subscribe(listener: () => void): () => void;
  /** Ask the server once per user; later calls are no-ops. */
  ensureLoaded(userId: string): void;
  /** Register; a call while one is in flight for the user returns the same promise. */
  register(userId: string, listingId: string): Promise<AssistRegisterOutcome>;
}

export function createAssistInterestStore(api: AssistInterestApi): AssistInterestStore {
  const statuses = new Map<string, AssistInterestStatus>();
  const registering = new Map<string, Promise<AssistRegisterOutcome>>();
  const listeners = new Set<() => void>();

  const set = (userId: string, status: AssistInterestStatus) => {
    if (statuses.get(userId) === status) return;
    statuses.set(userId, status);
    listeners.forEach((l) => l());
  };

  return {
    status: (userId) => statuses.get(userId) ?? 'unknown',

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    ensureLoaded(userId) {
      if ((statuses.get(userId) ?? 'unknown') !== 'unknown') return;
      set(userId, 'loading');
      api
        .getMine()
        .catch((): AssistMineOutcome => ({ ok: false, failure: 'failed' }))
        .then((outcome) => {
          // A registration that finished first has the final word.
          if (statuses.get(userId) === 'joined') return;
          if (outcome.ok) set(userId, outcome.joined ? 'joined' : 'none');
          else set(userId, outcome.failure === 'not_live' ? 'unavailable' : 'none');
        });
    },

    register(userId, listingId) {
      const inFlight = registering.get(userId);
      if (inFlight) return inFlight;
      const p = api
        .register(listingId)
        .catch((): AssistRegisterOutcome => ({ ok: false, failure: 'failed' }))
        .then((outcome) => {
          registering.delete(userId);
          if (outcome.ok) set(userId, 'joined');
          return outcome;
        });
      registering.set(userId, p);
      return p;
    },
  };
}
